/**
 * Componente compacto de seleção de mídia
 * 
 * Usado dentro de dialogs de configuração para permitir
 * que usuários selecionem arquivos da galeria de mídia
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, Check, Image, Video, Music, FileText, Upload } from 'lucide-react';
import { MediaPickerProps, MediaFileType, formatBytes } from '@/types/media';
import { useMediaGallery } from '@/hooks/useMediaGallery';
import { MediaUploadZone } from './MediaUploadZone';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';

const logger = createLogger('MediaGalleryPicker');

export function MediaGalleryPicker({
  onSelect,
  onCancel,
  allowedTypes = ['image', 'video', 'audio', 'document'],
  currentSelection,
}: MediaPickerProps) {
  const [selectedType, setSelectedType] = useState<MediaFileType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMediaId, setSelectedMediaId] = useState<string | undefined>(currentSelection);
  const [showUploadZone, setShowUploadZone] = useState(false);

  const {
    mediaList,
    isLoadingMedia,
    isUploading,
    uploadMedia,
    getMediaUrl,
  } = useMediaGallery({
    file_type: selectedType,
    search: searchQuery,
  });

  // Handler de upload de arquivo
  const handleUploadFile = (file: File) => {
    uploadMedia(file, {
      onSuccess: () => {
        logger.debug('Upload concluído, ocultando zona de upload');
        setShowUploadZone(false);
      },
    });
  };

  // Filtrar por tipos permitidos
  const filteredMedia = mediaList.filter((media) =>
    allowedTypes.includes(media.file_type)
  );

  // =====================================================
  // HANDLERS
  // =====================================================

  const handleSelect = (media: any) => {
    setSelectedMediaId(media.id);
  };

  const handleConfirm = () => {
    const selectedMedia = filteredMedia.find((m) => m.id === selectedMediaId);
    if (selectedMedia) {
      logger.debug('Mídia confirmada', { mediaId: selectedMedia.id });
      onSelect(selectedMedia);
    }
  };

  // =====================================================
  // RENDERIZAÇÃO
  // =====================================================

  // Renderizar preview miniatura
  const renderThumbnail = (media: any) => {
    const url = getMediaUrl(media.storage_path);

    if (!url) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <span className="text-base">
            {media.file_type === 'image' && '🖼️'}
            {media.file_type === 'video' && '🎥'}
            {media.file_type === 'audio' && '🎵'}
            {media.file_type === 'document' && '📄'}
          </span>
        </div>
      );
    }

    switch (media.file_type) {
      case 'image':
        return (
          <img
            src={url}
            alt={media.name}
            className="w-full h-full object-cover"
          />
        );
      case 'video':
        return (
          <video
            src={url}
            className="w-full h-full object-cover"
            muted
          />
        );
      default:
        return (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <span className="text-base">
              {media.file_type === 'audio' && '🎵'}
              {media.file_type === 'document' && '📄'}
            </span>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col h-[300px] sm:h-[350px] md:h-[400px]">
      {/* Filtros e busca */}
      <div className="space-y-2 pb-2 border-b">
        <Tabs
          value={selectedType}
          onValueChange={(value) => setSelectedType(value as MediaFileType | 'all')}
        >
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="all" className="text-xs">
              Todos
            </TabsTrigger>
            {allowedTypes.includes('image') && (
              <TabsTrigger value="image" className="text-xs">
                <Image className="h-3 w-3" />
              </TabsTrigger>
            )}
            {allowedTypes.includes('video') && (
              <TabsTrigger value="video" className="text-xs">
                <Video className="h-3 w-3" />
              </TabsTrigger>
            )}
            {allowedTypes.includes('audio') && (
              <TabsTrigger value="audio" className="text-xs">
                <Music className="h-3 w-3" />
              </TabsTrigger>
            )}
            {allowedTypes.includes('document') && (
              <TabsTrigger value="document" className="text-xs">
                <FileText className="h-3 w-3" />
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar arquivo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            type="button"
            variant={showUploadZone ? 'default' : 'outline'}
            size="sm"
            className="shrink-0"
            onClick={() => setShowUploadZone(!showUploadZone)}
          >
            <Upload className="h-4 w-4 mr-1" />
            Enviar
          </Button>
        </div>
      </div>

      {/* Zona de upload compacta */}
      {showUploadZone && (
        <div className="mt-2">
          <MediaUploadZone
            onUpload={handleUploadFile}
            isUploading={isUploading}
            className="p-4"
          />
        </div>
      )}

      {/* Lista de mídia - altura responsiva */}
      <ScrollArea className={cn(
        'mt-2',
        showUploadZone ? 'h-[100px] sm:h-[130px] md:h-[160px]' : 'h-[180px] sm:h-[230px] md:h-[280px]'
      )}>
        {isLoadingMedia && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoadingMedia && filteredMedia.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">
              {searchQuery
                ? 'Nenhum arquivo encontrado'
                : 'Nenhum arquivo na galeria'}
            </p>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="mt-2 text-xs"
              onClick={() => setShowUploadZone(true)}
            >
              <Upload className="h-3 w-3 mr-1" />
              Enviar um arquivo agora
            </Button>
          </div>
        )}

        {!isLoadingMedia && filteredMedia.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {filteredMedia.map((media) => (
              <div
                key={media.id}
                className={cn(
                  'group relative rounded-lg border overflow-hidden cursor-pointer',
                  'hover:shadow-md hover:scale-[1.02] transition-all',
                  selectedMediaId === media.id && 'ring-2 ring-primary ring-offset-2'
                )}
                onClick={() => handleSelect(media)}
              >
                {/* Thumbnail */}
                <div className="aspect-square relative bg-muted overflow-hidden">
                  {renderThumbnail(media)}
                  
                  {/* Check icon se selecionado */}
                  {selectedMediaId === media.id && (
                    <div className="absolute top-1 right-1 bg-primary rounded-full p-0.5">
                      <Check className="h-2 w-2 text-primary-foreground" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-1.5 space-y-0.5">
                  <p
                    className="text-xs font-medium truncate"
                    title={media.name}
                  >
                    {media.name}
                  </p>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="text-[10px] px-1 py-0">
                      {media.file_type}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">
                      {formatBytes(media.size_bytes)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Ações */}
      <div className="flex items-center justify-between gap-2 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={!selectedMediaId}
        >
          Selecionar
        </Button>
      </div>
    </div>
  );
}
